import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { Link } from "react-router-dom";
import rentalSpacesLottie from "../assets/lottie-files/rentalSpaces.lottie";
import mainMarketHallImg from "../assets/main_hall/main-hall-4.jpg";
import workshopRoomImg from "../assets/workshop_room/community-room-1.jpg";
import commercialKitchenImg from "../assets/kitchen/kitchen-4.jpg";
import MarketMap from "../assets/marketMap.png";
import { sanityFetch, sanityImageUrl } from "../sanityClient";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" as const },
  },
};
const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1, delayChildren: 0.1 } },
};

const LightCard = ({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) => (
  <div
    style={{
      background: "#ffffff",
      border: "1px solid rgba(45,106,20,0.12)",
      borderRadius: 16,
      padding: "1.75rem",
      height: "100%",
      boxShadow: "0 2px 20px rgba(0,0,0,0.055)",
      ...style,
    }}
  >
    {children}
  </div>
);

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <div style={{ marginBottom: "2rem" }}>
    <h2
      style={{
        fontSize: "clamp(1.6rem, 3vw, 2.2rem)",
        fontWeight: 800,
        color: "#0d1b2a",
        margin: 0,
      }}
    >
      {children}
    </h2>
    <div
      style={{
        width: 56,
        height: 4,
        background: "linear-gradient(90deg, #6dc744, #4a8c25)",
        borderRadius: 2,
        marginTop: 8,
      }}
    />
  </div>
);

interface SanityImage {
  asset: { _ref: string; _type: string };
}

interface SpaceCardData {
  cardImage?: SanityImage;
}

interface Space {
  name: string;
  image: string;
  description: string;
  link: string;
  accentColor: string;
}

const RentalSpaces: React.FC = () => {
  const [cardImages, setCardImages] = useState<{
    mainMarketHall?: SanityImage;
    workshopRoom?: SanityImage;
    commercialKitchen?: SanityImage;
  }>({});

  useEffect(() => {
    sanityFetch<{ mainMarketHall: SpaceCardData; workshopRoom: SpaceCardData; commercialKitchen: SpaceCardData }>(
      `{
        "mainMarketHall": *[_type == "mainMarketHall"][0]{cardImage},
        "workshopRoom": *[_type == "workshopRoom"][0]{cardImage},
        "commercialKitchen": *[_type == "commercialKitchen"][0]{cardImage}
      }`
    ).then((data) => {
      if (data)
        setCardImages({
          mainMarketHall: data.mainMarketHall?.cardImage,
          workshopRoom: data.workshopRoom?.cardImage,
          commercialKitchen: data.commercialKitchen?.cardImage,
        });
    });
  }, []);

  const spaces: Space[] = [
    {
      name: "Main Market Hall",
      image: cardImages.mainMarketHall
        ? sanityImageUrl(cardImages.mainMarketHall)
        : mainMarketHallImg,
      description:
        "A spacious, vibrant venue ideal for large markets, exhibitions, and community events.",
      link: "/rental-spaces/main-market-hall",
      accentColor: "#c6252d",
    },
    {
      name: "Workshop Room",
      image: cardImages.workshopRoom
        ? sanityImageUrl(cardImages.workshopRoom)
        : workshopRoomImg,
      description:
        "Perfect for classes, meetings, and small gatherings in a comfortable setting.",
      link: "/rental-spaces/workshop-room",
      accentColor: "#6dc744",
    },
    {
      name: "Commercial Kitchen",
      image: cardImages.commercialKitchen
        ? sanityImageUrl(cardImages.commercialKitchen)
        : commercialKitchenImg,
      description:
        "A fully equipped kitchen for culinary workshops, catering, or food prep.",
      link: "/rental-spaces/commercial-kitchen",
      accentColor: "#C19A3B",
    },
  ];

  return (
    <div>
      {/* ── HERO ── */}
      <section
        style={{
          background:
            "linear-gradient(135deg, #0a2605 0%, #0d1b2a 55%, #150308 100%)",
          padding: "5rem 1.5rem 6rem",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: -120,
            right: -120,
            width: 440,
            height: 440,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(198,37,45,0.2) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: -100,
            left: -100,
            width: 380,
            height: 380,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(109,199,68,0.15) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />

        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "3rem",
            position: "relative",
            zIndex: 1,
          }}
        >
          <motion.div
            style={{ flex: "1 1 480px", minWidth: 300 }}
            variants={stagger}
            initial="hidden"
            animate="visible"
          >
            <motion.span
              variants={fadeUp}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                background: "rgba(109,199,68,0.12)",
                border: "1px solid rgba(109,199,68,0.3)",
                borderRadius: 999,
                padding: "0.35rem 1rem",
                color: "#6dc744",
                fontSize: "0.82rem",
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                marginBottom: "1rem",
              }}
            >
              <span
                style={{
                  width: 7,
                  height: 7,
                  background: "#6dc744",
                  borderRadius: "50%",
                  display: "inline-block",
                }}
              />
              Book a Space
            </motion.span>

            <motion.h1
              variants={fadeUp}
              style={{
                fontSize: "clamp(2.4rem, 5.5vw, 4rem)",
                fontWeight: 900,
                color: "white",
                lineHeight: 1.08,
                marginBottom: "1.25rem",
              }}
            >
              Rent Our{" "}
              <span
                style={{
                  background: "linear-gradient(90deg, #6dc744, #c6252d)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Spaces
              </span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              style={{
                fontSize: "1.15rem",
                color: "rgba(255,255,255,0.72)",
                lineHeight: 1.7,
                marginBottom: "2rem",
                maxWidth: 520,
              }}
            >
              From intimate workshops to large community celebrations, our
              versatile spaces offer flexible layouts, modern amenities, and a
              vibrant atmosphere — perfect for bringing your ideas to life.
            </motion.p>

            <motion.div variants={fadeUp}>
              <a
                href="#spaces"
                style={{
                  display: "inline-block",
                  background: "linear-gradient(135deg, #c6252d, #a01e25)",
                  color: "white",
                  border: "none",
                  borderRadius: 999,
                  padding: "0.9rem 2.2rem",
                  fontWeight: 700,
                  fontSize: "1rem",
                  cursor: "pointer",
                  textDecoration: "none",
                  boxShadow: "0 4px 20px rgba(198,37,45,0.35)",
                  transition: "transform 0.2s, box-shadow 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 8px 28px rgba(198,37,45,0.5)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 4px 20px rgba(198,37,45,0.35)";
                }}
              >
                Explore Spaces →
              </a>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.88 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.85, ease: "easeOut" }}
            style={{
              flex: "1 1 360px",
              minWidth: 280,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <DotLottieReact
              src={rentalSpacesLottie}
              loop
              autoplay
              style={{ width: "100%", maxWidth: 440 }}
            />
          </motion.div>
        </div>
      </section>

      {/* ── SPACES CARDS ── */}
      <section
        id="spaces"
        style={{ background: "#fffdf8", padding: "5rem 1.5rem" }}
      >
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.div variants={fadeUp}>
              <SectionHeading>Available Spaces</SectionHeading>
            </motion.div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                gap: "2rem",
              }}
            >
              {spaces.map((space) => (
                <motion.div
                  key={space.name}
                  variants={fadeUp}
                  style={{ height: "100%" }}
                >
                  <LightCard
                    style={{
                      padding: 0,
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column",
                      borderTop: `4px solid ${space.accentColor}`,
                    }}
                  >
                    <img
                      src={space.image}
                      alt={space.name}
                      style={{ width: "100%", height: 200, objectFit: "cover" }}
                    />
                    <div
                      style={{
                        padding: "1.5rem",
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.5rem",
                      }}
                    >
                      <h3
                        style={{
                          margin: 0,
                          fontWeight: 800,
                          fontSize: "1.2rem",
                          color: "#0d1b2a",
                        }}
                      >
                        {space.name}
                      </h3>
                      <p
                        style={{
                          margin: 0,
                          color: "#4a5568",
                          lineHeight: 1.6,
                          fontSize: "0.95rem",
                          flex: 1,
                        }}
                      >
                        {space.description}
                      </p>
                      <Link
                        to={space.link}
                        style={{
                          display: "inline-block",
                          marginTop: "auto",
                          background:
                            "linear-gradient(135deg, #c6252d, #a01e25)",
                          color: "white",
                          borderRadius: 999,
                          padding: "0.6rem 1.4rem",
                          fontWeight: 700,
                          fontSize: "0.9rem",
                          textDecoration: "none",
                          textAlign: "center",
                          boxShadow: "0 3px 14px rgba(198,37,45,0.28)",
                          transition: "transform 0.2s, box-shadow 0.2s",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateY(-2px)";
                          e.currentTarget.style.boxShadow =
                            "0 6px 20px rgba(198,37,45,0.45)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow =
                            "0 3px 14px rgba(198,37,45,0.28)";
                        }}
                      >
                        View Details →
                      </Link>
                    </div>
                  </LightCard>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── MARKET MAP ── */}
      <section style={{ background: "#f2fbee", padding: "5rem 1.5rem" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.div variants={fadeUp} style={{ marginBottom: "2.5rem" }}>
              <SectionHeading>Explore Our Market Map</SectionHeading>
              <p
                style={{
                  color: "#4a5568",
                  fontSize: "1.05rem",
                  lineHeight: 1.7,
                  maxWidth: 640,
                  margin: 0,
                }}
              >
                Plan your visit and discover every corner of the St. John's
                Farmers' Market — where community, creativity, and fresh local
                produce come together.
              </p>
            </motion.div>

            <motion.div
              variants={fadeUp}
              style={{ display: "flex", justifyContent: "center" }}
            >
              <img
                src={MarketMap}
                alt="Market Map"
                style={{
                  width: "100%",
                  maxWidth: 800,
                  borderRadius: 16,
                  boxShadow: "0 4px 32px rgba(0,0,0,0.1)",
                  border: "1px solid rgba(45,106,20,0.12)",
                  transition: "transform 0.3s ease",
                  cursor: "pointer",
                }}
                onMouseOver={(e) =>
                  (e.currentTarget.style.transform = "scale(1.02)")
                }
                onMouseOut={(e) =>
                  (e.currentTarget.style.transform = "scale(1)")
                }
              />
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default RentalSpaces;
